import type {
  ApiRequestAIContent,
  ApiRequestAuthenticate,
  ApiRequestSearch,
  ApiRequestSubscription,
  ApiRequestTranscriptV2,
  ApiRequestUser,
  ApiResponseGlobalDictationOutcome,
  ApiResponseGlobalDictationTranscribe,
  ApiResponseGlobalDictationRewrite,
  RemoteConfigResponse,
  RpcAction,
  RpcRequestByAction,
  RpcResponseByAction,
} from "@/api.contract";
import type {
  ClientError,
  ClientResult,
  DictationDelivery,
  RemoteConfig,
  DictationClientPlatform,
  DictationTrigger,
  ServerErrorCode,
  SubscriptionPlan,
  Transcript,
  User,
} from "@/types";

export type ApiAuthMode = "required" | "optional" | "none";

export type ApiCallOptions = {
  timeoutMs?: number;
  keepalive?: boolean;
  signal?: AbortSignal;
  // Only the guest surfaces set this: they hold a guest token instead of a
  // signed-in session, and a 401 there must not sign anybody out.
  authToken?: string;
};

type TimeoutHandle = ReturnType<typeof setTimeout>;

export type ApiClientDependencies = {
  baseUrl: string;
  defaultTimeoutMs: number;
  getToken: () => string | undefined;
  onUnauthorized: () => void;
  fetch?: typeof globalThis.fetch;
  setTimeout?: (callback: () => void, timeoutMs: number) => TimeoutHandle;
  clearTimeout?: (handle: TimeoutHandle) => void;
};

type RequestOptions = ApiCallOptions & {
  auth: ApiAuthMode;
  authToken?: string;
  interruptOnUnauthorized?: boolean;
  headers?: HeadersInit;
  method: "GET" | "POST";
  body?: BodyInit;
  url: string;
};

type RpcData<Action extends RpcAction> = RpcResponseByAction<Action>["data"];
type RpcPayload<Action extends RpcAction> =
  RpcRequestByAction<Action>["payload"];
type TranscriptPayload<Type extends ApiRequestTranscriptV2["payload"]["type"]> =
  Extract<ApiRequestTranscriptV2["payload"], { type: Type }>;
type AuthenticatePayload<
  Type extends ApiRequestAuthenticate["payload"]["type"],
> = Extract<ApiRequestAuthenticate["payload"], { type: Type }>;
type AiContentPayload<Type extends ApiRequestAIContent["payload"]["type"]> =
  Extract<ApiRequestAIContent["payload"], { type: Type }>;

const SERVER_ERROR_CODES = [
  "InvalidEnvelope",
  "UnknownAction",
  "Unauthorized",
  "InvalidInput",
  "NotFound",
  "InvalidFilePath",
  "FileNotFound",
  "ExpectedMultipartFormData",
  "AudioFileRequired",
  "AudioFileTooLarge",
  "AudioDurationTooLong",
  "WebhookFailed",
  "Internal",
  "InvalidWebhookPayload",
  "InvalidAccessToken",
  "DeviceIdentityLinked",
  "InvalidEmail",
  "UserNotFound",
  "InvalidOneTimeCode",
  "OneTimeCodeExpired",
  "TranscriptNotFound",
  "UnsupportedFile",
  "OriginalFileUrlIsRequired",
  "UnableToGetUploadData",
  "NotAllowedToTranscribeFullAudio",
  "OnlyVoiceRecordingAndAudioOrVideoImportCanBeRetranscribed",
  "TranscriptAlreadyHasFullTranscript",
  "TranscriptNotCompleted",
  "UserNotSubscribed",
  "LiveRecordingUrlNotFound",
  "NoAudioFileAvailable",
  "AudioProcessingFailed",
  "TranscriptionFailed",
  "ImageTranscriptionFailed",
  "PdfTranscriptionFailed",
  "AlreadySubscribed",
  "SubscriptionNotFound",
  "PdfFailed",
  "TranslationFailed",
  "SummaryFailed",
  "FlashcardFailed",
  "QuizFailed",
  "RateLimitExceeded",
  "EmptyTranscription",
  "RewriteFailed",
] as const satisfies readonly ServerErrorCode[];

const _serverErrorCodes = new Set<string>(SERVER_ERROR_CODES);

const _isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const _isServerErrorCode = (value: unknown): value is ServerErrorCode =>
  typeof value === "string" && _serverErrorCodes.has(value);

const _isAbortError = (error: unknown): boolean =>
  _isRecord(error) && error.name === "AbortError";

const _failure = <Value>(error: ClientError): ClientResult<Value> => ({
  ok: false,
  error,
});

export const createApiClient = (dependencies: ApiClientDependencies) => {
  const fetchRequest = dependencies.fetch || globalThis.fetch.bind(globalThis);
  const scheduleTimeout =
    dependencies.setTimeout ||
    ((callback: () => void, timeoutMs: number): TimeoutHandle =>
      globalThis.setTimeout(callback, timeoutMs));
  const cancelTimeout =
    dependencies.clearTimeout ||
    ((handle: TimeoutHandle): void => globalThis.clearTimeout(handle));
  const baseUrl = dependencies.baseUrl.replace(/\/$/, "");

  const requestJson = async (
    options: RequestOptions,
  ): Promise<ClientResult<unknown>> => {
    const token =
      options.auth === "none"
        ? undefined
        : (options.authToken ?? dependencies.getToken() ?? undefined);

    if (options.auth === "required" && !token) {
      return _failure("unauthorized");
    }

    if (options.signal?.aborted) return _failure("cancelled");

    const abortController = new AbortController();
    let didTimeout = false;
    const timeoutMs = options.timeoutMs ?? dependencies.defaultTimeoutMs;
    const timeoutHandle = scheduleTimeout(() => {
      didTimeout = true;
      abortController.abort();
    }, timeoutMs);
    const cancelFromCaller = () => abortController.abort();
    options.signal?.addEventListener("abort", cancelFromCaller, { once: true });

    try {
      const headers = new Headers(options.headers);
      headers.set("Accept", "application/json");
      if (token) headers.set("Authorization", token);

      const response = await fetchRequest(options.url, {
        method: options.method,
        headers,
        body: options.body,
        keepalive: options.keepalive || undefined,
        signal: abortController.signal,
      });

      let body: unknown;
      try {
        body = await response.json();
      } catch {
        return _failure("invalidResponse");
      }

      const bodyError = _isRecord(body) ? body.error : undefined;
      const isUnauthorized =
        response.status === 401 ||
        response.status === 403 ||
        bodyError === "Unauthorized";

      if (isUnauthorized) {
        if (
          token &&
          options.auth !== "none" &&
          options.interruptOnUnauthorized !== false
        ) {
          dependencies.onUnauthorized();
        }
        return _failure("unauthorized");
      }

      if (_isRecord(body) && body.error !== undefined) {
        return _isServerErrorCode(body.error)
          ? _failure(body.error)
          : _failure("invalidResponse");
      }

      if (!response.ok) return _failure("invalidResponse");
      return { ok: true, value: body };
    } catch (error) {
      if (didTimeout) return _failure("timeout");
      if (options.signal?.aborted || _isAbortError(error)) {
        return _failure("cancelled");
      }
      if (error instanceof TypeError) return _failure("networkUnavailable");
      throw error;
    } finally {
      cancelTimeout(timeoutHandle);
      options.signal?.removeEventListener("abort", cancelFromCaller);
    }
  };

  const callRpc = async <Action extends RpcAction>(
    action: Action,
    payload: RpcPayload<Action>,
    auth: ApiAuthMode,
    options: ApiCallOptions = {},
    authOverride?: { token?: string; interruptOnUnauthorized: boolean },
  ): Promise<ClientResult<RpcData<Action>>> => {
    const response = await requestJson({
      ...options,
      auth,
      authToken: authOverride?.token,
      interruptOnUnauthorized: authOverride?.interruptOnUnauthorized,
      method: "POST",
      url: baseUrl,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event: { action, payload } }),
    });
    if ("error" in response) return _failure(response.error);
    if (!_isRecord(response.value) || response.value.action !== action) {
      return _failure("invalidResponse");
    }
    if (response.value.data !== undefined && !_isRecord(response.value.data)) {
      return _failure("invalidResponse");
    }
    return {
      ok: true,
      value: response.value.data as RpcData<Action>,
    };
  };

  const auth = {
    guest: (
      payload: AuthenticatePayload<"OnGuestSignIn">["payload"],
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "Authenticate",
        { type: "OnGuestSignIn", payload },
        "none",
        options,
      ),
    google: (
      payload: AuthenticatePayload<"OnGoogleSignIn">["payload"],
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "Authenticate",
        { type: "OnGoogleSignIn", payload },
        "none",
        options,
      ),
    requestOneTimeCode: (
      payload: AuthenticatePayload<"OnOneTimeCodeRequested">["payload"],
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "Authenticate",
        { type: "OnOneTimeCodeRequested", payload },
        "none",
        options,
      ),
    confirmOneTimeCode: (
      payload: AuthenticatePayload<"OnOneTimeCodeSubmit">["payload"],
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "Authenticate",
        { type: "OnOneTimeCodeSubmit", payload },
        "none",
        options,
      ),
    exchangeDesktopCode: (
      payload: AuthenticatePayload<"OnDesktopCodeExchange">["payload"],
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "Authenticate",
        { type: "OnDesktopCodeExchange", payload },
        "none",
        options,
      ),
  };

  const agentCallback = (result: ClientResult<RpcData<"Agent">>): ClientResult<RpcData<"Agent">> => {
    if ("error" in result) return result;
    if (typeof result.value?.redirectUrl !== "string" || !result.value.redirectUrl) return _failure("invalidResponse");
    try {
      const target = new URL(result.value.redirectUrl);
      if (target.username || target.password || (target.protocol !== "https:" && !(target.protocol === "http:" && ["127.0.0.1", "localhost", "[::1]"].includes(target.hostname)))) return _failure("invalidResponse");
    } catch { return _failure("invalidResponse"); }
    return result;
  };
  const agent = {
    describeConnection: (connectionId: string, options?: ApiCallOptions) =>
      callRpc("Agent", { type: "login.describe", payload: { connectionId } }, "none", options),
    cancelConnection: async (connectionId: string, error: "access_denied" | "server_error", options?: ApiCallOptions) =>
      agentCallback(await callRpc("Agent", { type: "login.cancel", payload: { connectionId, error } }, "none", options)),
    authorize: async (connectionId: string, options?: ApiCallOptions): Promise<ClientResult<RpcData<"Agent">>> => {
      const result = await callRpc("Agent", { type: "login.authorize", payload: { connectionId } }, "required", options);
      if ("error" in result) return result;
      return result.value?.status === "connected" ? agentCallback(result) : _failure("invalidResponse");
    },
    listConnections: (nextCursor?: string, options?: ApiCallOptions) =>
      callRpc("Agent", { type: "connections.list", ...(nextCursor ? { payload: { nextCursor } } : {}) }, "required", options),
    disconnect: (connectionId: string, options?: ApiCallOptions) =>
      callRpc("Agent", { type: "connections.disconnect", payload: { connectionId } }, "required", options),
  };

  const user = {
    delete: (options?: ApiCallOptions) =>
      callRpc("User", { type: "delete" }, "required", options),
    setAiConsent: (accepted: boolean, options?: ApiCallOptions) =>
      callRpc("User", { type: "aiConsent", payload: { accepted, version: 1 } }, "required", options),
    get: (options?: ApiCallOptions) =>
      callRpc("User", { type: "get" }, "required", options),
    update: (updates: Partial<User>, options?: ApiCallOptions) =>
      callRpc(
        "User",
        { type: "update", payload: { updates } },
        "required",
        options,
      ),
    captureAcquisition: (
      attribution: Record<string, string>,
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "User",
        { type: "captureAcquisition", payload: { attribution } },
        "required",
        options,
      ),
    desktopAuthorize: (challenge: string, options?: ApiCallOptions) =>
      callRpc(
        "User",
        { type: "desktopAuthorize", payload: { challenge } },
        "required",
        options,
      ),
    requestWebSignIn: (email: string, options?: ApiCallOptions) =>
      callRpc(
        "User",
        {
          type: "webSignIn",
          payload: { action: "request", payload: { email } },
        },
        "required",
        options,
      ),
    confirmWebSignIn: (oneTimeCode: number, options?: ApiCallOptions) =>
      callRpc(
        "User",
        {
          type: "webSignIn",
          payload: { action: "confirm", payload: { oneTimeCode } },
        },
        "required",
        options,
      ),
    webSignInWithGoogle: (
      googleAccessToken: string,
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "User",
        {
          type: "webSignIn",
          payload: { action: "google", payload: { googleAccessToken } },
        },
        "required",
        options,
      ),
    attachWebAccount: (accessToken: string, options?: ApiCallOptions) =>
      callRpc(
        "User",
        {
          type: "webSignIn",
          payload: { action: "attach", payload: { accessToken } },
        },
        "required",
        options,
      ),
    requestNativeSignIn: (email: string, options?: ApiCallOptions) =>
      callRpc(
        "User",
        {
          type: "nativeSignIn",
          payload: { action: "request", payload: { email } },
        },
        "required",
        options,
      ),
    confirmNativeSignIn: (oneTimeCode: number, options?: ApiCallOptions) =>
      callRpc(
        "User",
        {
          type: "nativeSignIn",
          payload: { action: "confirm", payload: { oneTimeCode } },
        },
        "required",
        options,
      ),
  };

  const transcript = {
    create: (request: TranscriptPayload<"create">, options?: ApiCallOptions) =>
      callRpc("TranscriptV2", request, "required", options),
    get: (id: string, options?: ApiCallOptions) =>
      callRpc(
        "TranscriptV2",
        { type: "get", payload: { id } },
        "required",
        options,
      ),
    getWithGuestToken: (
      id: string,
      guestToken: string,
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "TranscriptV2",
        { type: "get", payload: { id } },
        "required",
        options,
        { token: guestToken, interruptOnUnauthorized: false },
      ),
    list: (nextCursor?: string, options?: ApiCallOptions) =>
      callRpc(
        "TranscriptV2",
        { type: "list", payload: nextCursor ? { nextCursor } : undefined },
        "required",
        options,
      ),
    update: (
      id: string,
      updates: Partial<Transcript>,
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "TranscriptV2",
        { type: "update", payload: { id, updates } },
        "required",
        options,
      ),
    set: (transcriptValue: Transcript, options?: ApiCallOptions) =>
      callRpc(
        "TranscriptV2",
        {
          type: "set",
          payload: { id: transcriptValue.id, transcript: transcriptValue },
        },
        "required",
        options,
      ),
    delete: (id: string, options?: ApiCallOptions) =>
      callRpc(
        "TranscriptV2",
        { type: "delete", payload: { id } },
        "required",
        options,
      ),
    listDeleted: (nextCursor?: string, options?: ApiCallOptions) =>
      callRpc(
        "TranscriptV2",
        { type: "listDeleted", payload: { nextCursor } },
        "required",
        options,
      ),
    restore: (id: string, options?: ApiCallOptions) =>
      callRpc(
        "TranscriptV2",
        { type: "restore", payload: { id } },
        "required",
        options,
      ),
    deleteForever: (id: string, options?: ApiCallOptions) =>
      callRpc(
        "TranscriptV2",
        { type: "deleteForever", payload: { id } },
        "required",
        options,
      ),
    getUploadData: (fileName: string, options?: ApiCallOptions) =>
      callRpc(
        "TranscriptV2",
        { type: "getUploadData", payload: { fileName } },
        "required",
        options,
      ),
    createMultipartUpload: (
      fileName: string,
      fileSize: number,
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "TranscriptV2",
        {
          type: "multipartUpload",
          payload: { action: "create", fileName, fileSize },
        },
        "required",
        options,
      ),
    getMultipartUploadPartUrl: (
      input: {
        objectKey: string;
        uploadId: string;
        partNumber: number;
        fileSize: number;
      },
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "TranscriptV2",
        { type: "multipartUpload", payload: { action: "signPart", ...input } },
        "required",
        options,
      ),
    completeMultipartUpload: (
      input: { objectKey: string; uploadId: string; fileSize: number },
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "TranscriptV2",
        { type: "multipartUpload", payload: { action: "complete", ...input } },
        "required",
        options,
      ),
    abortMultipartUpload: (
      input: { objectKey: string; uploadId: string },
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "TranscriptV2",
        { type: "multipartUpload", payload: { action: "abort", ...input } },
        "required",
        options,
      ),
    transcribeFullAudio: (transcriptId: string, options?: ApiCallOptions) =>
      callRpc(
        "TranscriptV2",
        {
          type: "transcribe",
          payload: { action: "transcribeFullAudio", transcriptId },
        },
        "required",
        options,
      ),
    addSpeaker: (transcriptId: string, options?: ApiCallOptions) =>
      callRpc(
        "TranscriptV2",
        {
          type: "transcribe",
          payload: { action: "addSpeaker", transcriptId },
        },
        "required",
        options,
      ),
    liveRecordingChunk: (
      request: TranscriptPayload<"liveRecordingChunk">,
      options?: ApiCallOptions,
    ) =>
      callRpc("TranscriptV2", request, "required", options, {
        interruptOnUnauthorized: false,
      }),
  };

  const aiContent = {
    markdownToPdf: (
      request: AiContentPayload<"markdownToPdf">,
      options?: ApiCallOptions,
    ) => callRpc("AIContent", request, "required", options),
    addTranslation: (
      transcriptId: string,
      language: string,
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "AIContent",
        { type: "addTranslation", transcriptId, language },
        "required",
        options,
      ),
    addSummary: (
      transcriptId: string,
      instructions?: string,
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "AIContent",
        instructions
          ? { type: "addSummary", transcriptId, instructions }
          : { type: "addSummary", transcriptId },
        "required",
        options,
      ),
    addFlashcard: (
      transcriptId: string,
      instructions?: string,
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "AIContent",
        instructions
          ? { type: "addFlashcard", transcriptId, instructions }
          : { type: "addFlashcard", transcriptId },
        "required",
        options,
      ),
    addQuiz: (
      transcriptId: string,
      instructions?: string,
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "AIContent",
        instructions
          ? { type: "addQuiz", transcriptId, instructions }
          : { type: "addQuiz", transcriptId },
        "required",
        options,
      ),
  };

  const subscription = {
    checkout: (
      baseAppUrl: string,
      subscriptionPlan: SubscriptionPlan,
      attribution?: Record<string, string>,
      options?: ApiCallOptions,
    ) =>
      callRpc(
        "Subscription",
        {
          type: "checkout",
          payload: { baseAppUrl, subscriptionPlan, attribution },
        },
        "required",
        options,
      ),
    status: (options?: ApiCallOptions) =>
      callRpc("Subscription", { type: "status" }, "required", options),
    billingPortal: (baseAppUrl: string, options?: ApiCallOptions) =>
      callRpc(
        "Subscription",
        { type: "billingPortal", payload: { baseAppUrl } },
        "required",
        options,
      ),
    annualUpgradeOffer: (options?: ApiCallOptions) =>
      callRpc(
        "Subscription",
        { type: "annualUpgradeOffer" },
        "required",
        options,
      ),
    annualUpgrade: (baseAppUrl: string, options?: ApiCallOptions) =>
      callRpc(
        "Subscription",
        { type: "annualUpgrade", payload: { baseAppUrl } },
        "required",
        options,
      ),
  };

  const search = {
    query: (payload: ApiRequestSearch["payload"], options?: ApiCallOptions) =>
      callRpc("Search", payload, "required", options),
  };

  const remoteConfig = {
    get: async (
      query: { platform?: string; version?: string } = {},
      options: ApiCallOptions = {},
    ): Promise<ClientResult<RemoteConfigResponse>> => {
      const url = new URL(`${baseUrl}/remote-config`);
      for (const [key, value] of Object.entries(query)) {
        if (value) url.searchParams.set(key, value);
      }
      const response = await requestJson({
        ...options,
        auth: "optional",
        method: "GET",
        url: url.toString(),
      });
      if ("error" in response) return _failure(response.error);
      if (!_isRecord(response.value)) return _failure("invalidResponse");
      const value = response.value;
      if (
        typeof value.country !== "string" ||
        !_isRecord(value.webPlans) ||
        typeof value.webPlans.monthly !== "string" ||
        typeof value.webPlans.annual !== "string"
      ) {
        return _failure("invalidResponse");
      }
      return {
        ok: true,
        value: {
          country: value.country,
          webPlans: {
            monthly: value.webPlans.monthly,
            annual: value.webPlans.annual,
          },
        } as RemoteConfig,
      };
    },
  };

  const dictation = {
    transcribe: async (
      params: {
        audio: Blob;
        fileName?: string;
        prompt?: string;
        durationMs?: number;
        platform: DictationClientPlatform;
        trigger: DictationTrigger;
      },
      options: ApiCallOptions = {},
    ): Promise<ClientResult<ApiResponseGlobalDictationTranscribe["data"]>> => {
      const formData = new FormData();
      const fileName =
        params.fileName ||
        (params.audio.type.includes("webm")
          ? "dictation.webm"
          : params.audio.type.includes("mp4") ||
              params.audio.type.includes("m4a")
            ? "dictation.m4a"
            : "dictation.wav");
      formData.append("audio", params.audio, fileName);
      formData.append("platform", params.platform);
      formData.append("trigger", params.trigger);
      if (params.prompt) formData.append("prompt", params.prompt);
      if (params.durationMs !== undefined) {
        formData.append("durationMs", String(params.durationMs));
      }

      const response = await requestJson({
        ...options,
        auth: "required",
        // A guest dictates before any account exists, so the caller supplies
        // the guest token and a 401 must not sign anybody out.
        ...(options.authToken ? { interruptOnUnauthorized: false } : {}),
        method: "POST",
        url: `${baseUrl}/global-dictation/transcribe`,
        body: formData,
      });
      if ("error" in response) return _failure(response.error);
      if (!_isRecord(response.value)) return _failure("invalidResponse");
      if (
        response.value.data !== undefined &&
        !_isRecord(response.value.data)
      ) {
        return _failure("invalidResponse");
      }
      return {
        ok: true,
        value: response.value
          .data as ApiResponseGlobalDictationTranscribe["data"],
      };
    },
    rewrite: async (
      params: {
        text: string;
        action: "cleanUp" | "simplify";
        platform: DictationClientPlatform;
      },
      options: ApiCallOptions = {},
    ): Promise<ClientResult<ApiResponseGlobalDictationRewrite["data"]>> => {
      const response = await requestJson({
        ...options,
        auth: "required",
        method: "POST",
        url: `${baseUrl}/global-dictation/rewrite`,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });
      if ("error" in response) return _failure(response.error);
      if (!_isRecord(response.value) || !_isRecord(response.value.data)) {
        return _failure("invalidResponse");
      }
      return {
        ok: true,
        value: response.value.data as ApiResponseGlobalDictationRewrite["data"],
      };
    },
    outcome: async (
      params: {
        platform: DictationClientPlatform;
        trigger: DictationTrigger;
        delivery: DictationDelivery;
      },
      options: ApiCallOptions = {},
    ): Promise<ClientResult<ApiResponseGlobalDictationOutcome["data"]>> => {
      const response = await requestJson({
        ...options,
        auth: "required",
        method: "POST",
        url: `${baseUrl}/global-dictation/outcome`,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });
      if ("error" in response) return _failure(response.error);
      if (!_isRecord(response.value) || !_isRecord(response.value.data)) {
        return _failure("invalidResponse");
      }
      return {
        ok: true,
        value: response.value.data as ApiResponseGlobalDictationOutcome["data"],
      };
    },
  };

  return {
    auth,
    agent,
    user,
    transcript,
    aiContent,
    subscription,
    search,
    remoteConfig,
    dictation,
  };
};

export type ApiClient = ReturnType<typeof createApiClient>;
